<script setup lang="ts">
import { Components } from '@/api/generated/api';
import CheckMarkIcon from '@/assets/icons/CheckMarkIcon.vue';
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import LightBulbIcon from '@/assets/icons/LightBulbIcon.vue';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import ClickableListItem from '@/core/elements/ClickableListItem.vue';
import CustomSeparator from '@/core/elements/CustomSeparator.vue';
import {
  formatGradeCalculationTagWeightMultiplier,
  getGradeCalculationTagTranslationKey,
  isDefaultGradeCalculationTag,
  isExcludedGradeCalculationTag,
} from '@/utils/gradeCalculation.utils';
import { EPosition } from 'common/enum/misc.enum';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import GradeCalculationTagIcon from './GradeCalculationTagIcon.vue';
import { useMobile } from '@/composables/useMobile';

const emit = defineEmits<{
  'select:tag': [tagId: number | null];
}>();

const props = withDefaults(
  defineProps<{
    currentTag: Components.Schemas.GradeCalculationTagDto | null;
    disabled?: boolean;
    gradeTags: Components.Schemas.GradeCalculationTagDto[];
    readOnly?: boolean;
    variant?: 'default' | 'chip';
  }>(),
  {
    disabled: false,
    readOnly: false,
    variant: 'default',
  },
);

const { t } = useI18n();
const { isMobile } = useMobile();
const buttonRef = useTemplateRef<HTMLElement>('buttonRef');
const dialogRef = useTemplateRef('dialogRef');
const isMenuVisible = ref(false);

const selectedTag = computed<Components.Schemas.GradeCalculationTagDto | null>(() => {
  return isDefaultGradeCalculationTag(props.currentTag) ? null : props.currentTag;
});

const selectableTags = computed<Components.Schemas.GradeCalculationTagDto[]>(() => {
  return props.gradeTags.filter((tag) => !tag.isDefault);
});

const selectedLabel = computed<string>(() => {
  return t(getGradeCalculationTagTranslationKey(selectedTag.value?.key));
});

const isNormalWeightingSelected = computed<boolean>(() => !selectedTag.value);

const showMenu = async (): Promise<void> => {
  if (props.disabled || props.readOnly) return;
  isMenuVisible.value = true;
  await dialogRef.value?.show();
};

const hideMenu = (): void => {
  isMenuVisible.value = false;
  dialogRef.value?.hide();
};

const toggleMenu = async (): Promise<void> => {
  if (isMenuVisible.value) {
    hideMenu();
    return;
  }
  await showMenu();
};

const handleSelectTag = async (tagId: number | null): Promise<void> => {
  hideMenu();
  emit('select:tag', tagId);
};

const isSelectedTag = (tag: Components.Schemas.GradeCalculationTagDto): boolean => {
  return selectedTag.value?.id === tag.id;
};
</script>

<template>
  <div class="relative flex items-center">
    <component
      :is="props.readOnly ? 'span' : 'button'"
      ref="buttonRef"
      :type="props.readOnly ? undefined : 'button'"
      :class="[
        'inline-flex items-center gap-sm overflow-hidden rounded-badge px-[12px] py-xs transition duration-200',
        props.variant === 'chip'
          ? 'h-chip border-0 bg-surface-tertiary text-footer font-semibold leading-[1.6] tracking-[0.12px]'
          : 'h-[35px] border border-divider bg-surface-primary text-body',
        props.readOnly
          ? 'cursor-default'
          : ['state-effects', disabled ? 'cursor-default opacity-50' : 'cursor-pointer hover:bg-surface-secondary'],
      ]"
      @click.stop="toggleMenu"
    >
      <GradeCalculationTagIcon :tag-key="selectedTag?.key" size="sm" />
      <span class="flex items-center gap-1 whitespace-nowrap">
        <span
          :class="[props.variant === 'chip' ? 'font-semibold leading-[1.6]' : 'font-normal leading-[1.7]']"
          class="text-primary"
        >
          {{ selectedLabel }}
        </span>
      </span>
      <ChevronDownIcon
        v-if="!props.readOnly"
        :class="[
          'shrink-0 text-secondary transition-transform duration-200',
          props.variant === 'chip' ? 'size-icon-xs' : 'size-icon-sm',
        ]"
      />
    </component>

    <PositionablePopover
      v-if="!props.readOnly"
      ref="dialogRef"
      :anchor-element="buttonRef"
      dialog-style="!rounded-group"
      content-style="!rounded-group"
      flip-strategy="vertical"
      :offset-y="8"
      :position="isMobile ? EPosition.BOTTOM_CENTER : EPosition.BOTTOM_RIGHT"
      @hide="isMenuVisible = false"
    >
      <div class="flex w-[352px] flex-col gap-px rounded-group bg-surface-primary p-md">
        <ClickableListItem class="min-h-button !gap-3 !rounded-sm px-sm py-sm" @click="void handleSelectTag(null)">
          <div class="size-icon-md shrink-0" />
          <span class="mr-auto min-w-0 text-body font-normal leading-[1.7] text-primary">
            {{ $t('gradeCalculation.tags.default') }}
          </span>
          <CheckMarkIcon v-if="isNormalWeightingSelected" class="size-cell-icon shrink-0 text-tint" />
        </ClickableListItem>

        <CustomSeparator class="mx-sm my-sm" />

        <ClickableListItem
          v-for="tag in selectableTags"
          :key="tag.id"
          class="min-h-button !gap-3 !rounded-sm px-sm py-sm"
          @click="void handleSelectTag(tag.id)"
        >
          <GradeCalculationTagIcon :tag-key="tag.key" reserve-space size="md" />
          <span class="mr-auto min-w-0 text-body font-normal leading-[1.7] text-primary">
            <span>{{ $t(getGradeCalculationTagTranslationKey(tag.key)) }}</span>
            <span v-if="!isExcludedGradeCalculationTag(tag)" class="text-secondary">
              ({{ formatGradeCalculationTagWeightMultiplier(tag.weightMultiplier) }})</span
            >
          </span>
          <CheckMarkIcon v-if="isSelectedTag(tag)" class="size-cell-icon shrink-0 text-tint" />
        </ClickableListItem>

        <CustomSeparator class="mx-sm my-sm" />

        <div class="flex items-start gap-sm px-sm py-sm">
          <LightBulbIcon class="mt-[1px] size-icon-md shrink-0 text-warning" />
          <p class="text-footer font-normal leading-[1.6] tracking-[0.12px] text-primary text-left">
            {{ $t('gradeCalculation.entryTagDescription') }}
          </p>
        </div>
      </div>
    </PositionablePopover>
  </div>
</template>
